import { useEffect, useRef } from 'react';

import dayjs from 'dayjs';
import { useShallow } from 'zustand/react/shallow';

import { useReceiveStore } from '~/store';
import { getDateByOffset } from '~/utils/date';

import type { TypeReceiveRequestItemErrorState, TypeReceiveRequestRegisterModalGoods } from '~/types/receive.types';

/**
 * [입고관리 > 입고요청관리] 입고요청 등록 모달 > 입고 상품 테이블 행 유효성검사 커스텀 훅
 */
const useReceiveRequestRegisterTableRowValidation = () => {
  const [
    requestModalGoodsList,
    requestModalGoodsListStatus,
    setRequestModalGoodsListStatus,
    resetRequestModalGoodsListStatus,
  ] = useReceiveStore(
    useShallow((state) => [
      state.requestModalGoodsList,
      state.requestModalGoodsListStatus,
      state.setRequestModalGoodsListStatus,
      state.resetRequestModalGoodsListStatus,
    ])
  );

  const currentRowCount = requestModalGoodsList.length;
  const previousRowCount = useRef(currentRowCount);
  const firstErrorRowIndexRef = useRef<number | null>(null);

  const resetErrors = () => {
    resetRequestModalGoodsListStatus();
    firstErrorRowIndexRef.current = null;
  };

  useEffect(() => {
    return () => {
      resetErrors();
    };
  }, []);

  useEffect(() => {
    // 상품이 추가되는 경우, error 상태값 초기화
    if (currentRowCount > previousRowCount.current) {
      resetErrors();
    }
    previousRowCount.current = currentRowCount;
  }, [currentRowCount]);

  const validateSingleRow = (goodsItem: TypeReceiveRequestRegisterModalGoods): TypeReceiveRequestItemErrorState => {
    const hasDateExistError = goodsItem.dateUseYn && !goodsItem.date;
    const hasDateRangeError =
      goodsItem.dateUseYn &&
      goodsItem.date &&
      goodsItem.dateType === 'expirationDate' &&
      dayjs(goodsItem.date).isBefore(getDateByOffset(0, 'day'), 'day');
    const hasDateError = hasDateExistError || hasDateRangeError;
    const hasQuantityError = !goodsItem.boxQty && !goodsItem.eachQty;
    const hasBoxPerQuantityError = !goodsItem.boxPerQty;

    return {
      date: hasDateError ? 'error' : 'default',
      boxQty: hasQuantityError ? 'error' : 'default',
      boxPerQty: hasBoxPerQuantityError ? 'error' : 'default',
      eachQty: hasQuantityError ? 'error' : 'default',
    };
  };

  const validateAllRows = (): { errorList: TypeReceiveRequestItemErrorState[]; firstErrorRowIndex: number | null } => {
    const errorList = requestModalGoodsList.map(validateSingleRow);
    const _firstErrorIndex = errorList.findIndex((error) => Object.values(error).includes('error'));

    const firstErrorIndex = _firstErrorIndex !== -1 ? _firstErrorIndex : null;

    setRequestModalGoodsListStatus(errorList);
    firstErrorRowIndexRef.current = firstErrorIndex;

    return {
      errorList,
      firstErrorRowIndex: firstErrorIndex,
    };
  };

  return {
    errorList: requestModalGoodsListStatus,
    setErrorList: setRequestModalGoodsListStatus,
    validateAllRows,
  };
};

export default useReceiveRequestRegisterTableRowValidation;
